<template>
  <div class="admin-call-position-detail bg-white-custom border px-3 py-3">
    <b-overlay :show="isLoading" rounded="sm">
      <div
        v-if="positionId && positionInfo.assigned_user"
        class="admin-call-position-detail--header d-flex align-items-center"
      >
        <h2 class="mb-0 text-nowrap">
          {{ `Assigned to ${positionInfo.assigned_user}` }}
        </h2>
      </div>
      <hr v-if="positionId && positionInfo.assigned_user" />
      <ConveyRequiredStatement class="mb-2" />
      <div v-if="errorMsg" class="text-danger mb-2">{{ errorMsg }}</div>
      <div class="admin-call-position-detail--body">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group
              id="fieldset-sip-id"
              label="SIP ID:"
              style="font-weight:500"
              label-for="input-sip-id"
              class="required-form-field"
            >
              <b-form-input
                id="input-sip-id"
                v-model="positionInfo.sip_id"
                trim
                required
              />
              <b-form-invalid-feedback :state="!!errors['sip_id'] ? false : true">
                {{ errors['sip_id'] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group
              id="fieldset-position-name"
              label="Name:"
              style="font-weight:500"
              label-for="input-position-name"
            >
              <b-form-input
                id="input-position-name"
                v-model="positionInfo.position_name"
                trim
              />
            </b-form-group>
          </b-col>
          <b-col v-if="positionId" cols="12" md="6">
            <b-form-group
              id="fieldset-created-at"
              label="Created At:"
              style="font-weight:500"
              label-for="input-created-at"
            >
              <b-form-input
                id="input-created-at"
                :value="positionInfo.created_at"
                trim
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col v-if="positionId" cols="12" md="6">
            <b-form-group
              id="fieldset-updated-at"
              label="Updated At:"
              style="font-weight:500"
              label-for="input-updated-at"
            >
              <b-form-input
                id="input-updated-at"
                :value="positionInfo.updated_at"
                trim
                disabled
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <div class="admin-call-position-detail--footer d-flex align-items-center justify-content-end gap-2">
        <b-button
          variant="success"
          pill
          size="sm"
          :disabled="savePending || deletePending"
          @click="savePosition()"
        >
          <b-spinner v-if="savePending" small></b-spinner>
          <span v-else>{{ positionId ? 'Save' : 'Add' }}</span>
        </b-button>
        <b-button
          v-if="positionId"
          pill
          variant="danger"
          size="sm"
          :disabled="savePending || deletePending"
          @click="removePosition()"
        >
          <b-spinner v-if="deletePending" small></b-spinner>
          <span v-else>Delete</span>
        </b-button>
      </div>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';
import { mapGetters } from "vuex";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import ConveyRequiredStatement from "@/components/ConveyRequiredStatement";

export default {
  name: "AdminCallPositionDetail",
  mixins: [formatter, errorHandler],
  components: { ConveyRequiredStatement },
  props: {
    positionId: String,
  },
  data() {
    return {
      isLoading: false,
      savePending: false,
      deletePending: false,
      positionInfo: {},
      errors: {},
      errorMsg: null,
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("agency", ["currentAgencyId"]),
  },
  created() {
    if (this.positionId) {
      this.fetchPosition();
    }
  },
  methods: {
    async savePosition() {
      this.errors = {};
      const sipIdValidateRegex = /^[a-zA-Z0-9_-]{1,12}$/;
      if (!this.positionInfo.sip_id) {
        this.errors['sip_id'] = "SIP ID is required";
        return;
      } else if (!sipIdValidateRegex.test(this.positionInfo.sip_id)) {
        this.errors['sip_id'] = "Invalid SIP ID. Please input SIP ID in valid format.";
        return;
      }

      const payload = {
        sip_id: this.positionInfo.sip_id,
        position_name: this.positionInfo.position_name,
      };

      if (this.positionId) {
        try {
          this.savePending = true;
          await this.$ConveyApi.agenciesApi.updateAgencyPosition({ payload, positionId: this.positionId });
          this.$toastr("success", "Call position has been updated.");
          this.$emit("updated");
        } catch(err) {
          console.log("[AdminCallPositionDetail:UpdatePosition] ERROR: ", err);
          if (err.status === 400) {
            if (err.response.data && err.response.data.data && err.response.data.data.error) {
              this.$toastr("error", err.response.data.data.error);
            } else {
              this.$toastr("error", "Error encountered updating call position.");
            }
          } else {
            this.$toastr("error", "Error encountered updating call position.");
          }
        } finally {
          this.savePending = false;
        }
      } else {
        try {
          this.savePending = true;
          await this.$ConveyApi.agenciesApi.createAgencyPosition({ payload });
          this.$toastr("success", `Call position "${this.positionInfo.position_name}" has been created.`);
          this.$emit("created");
        } catch(err) {
          console.log("[AdminCallPositionDetail:CreatePosition] ERROR: ", err);
          if (err.status === 400) {
            if (err.response.data && err.response.data.data && err.response.data.data.error) {
              this.$toastr("error", err.response.data.data.error);
            } else {
              this.$toastr("error", "Error occurred addming new call position to your agency");
            }
          } else {
            this.$toastr("error", "Error occurred addming new call position to your agency");
          }
        } finally {
          this.savePending = false;
        }
      }
    },
    async removePosition() {
      if (!confirm(`Are you sure you want to remove "${this.positionInfo.position_name}" call position from your agency?`)) {
        return;
      }

      try {
        this.deletePending = true;
        await this.$ConveyApi.agenciesApi.removeAgencyPosition({ positionId: this.positionId });
        this.$toastr("success", "Call position has been removed from your agency.");
        this.$emit("deleted");
      } catch(err) {
        console.log("[AdminCallPositionsList:removePosition] ERROR: ", err);
        this.$toastr("error", "Error occurred removing call position from your agency");
      } finally {
        this.deletePending = false;
      }
    },
    async fetchPosition() {
      try {
        this.fetchAbortController = new AbortController();
        this.isLoading = true;
        const res = await this.$ConveyApi.agenciesApi.getAgencyPosition({ positionId: this.positionId });
        const { position_id, position_name, created_at, updated_at, sip_id, assigned_user } = res.data.data;
        this.positionInfo = {
          sip_id,
          position_id,
          position_name,
          assigned_user: assigned_user.trim(),
          created_at: this.formattedDate(created_at),
          updated_at: this.formattedDate(updated_at),
        };
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminCallPositionDetail:fetchPosition] ERROR: ", err);
        }
      } finally {
        this.isLoading = false;
      }
    },
    formattedDate(date) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formattedDate = moment.utc(date).tz(tz).format("MMM D, YYYY h:mm a");
      return formattedDate;
    },
  },
  beforeDestroy() {
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
      this.fetchAbortController = null;
    }
  }
};
</script>

<style lang="scss">
.admin-call-position-detail {
  max-width: 800px;
  width: 100%;
  height: auto;
  border-radius: 1rem;

  &--header {

  }

  &--body {
  }

  &--footer {}
}
</style>